import React from 'react';
import { Column, TableState } from 'react-table';
import { Table } from '@brighterion/br-ui';
import { DateService, NumberService } from 'Services';
import _ from 'lodash';
import Pagination from '../Pagination';
import styles from './index.css';
import { ITableFilter, QFContainer } from '../types';
import { TableDropDown } from '../TableDropdown/TableDropDown';

interface Props<D extends object> {
    state: TableState<D>;
    paginationProps: PaginationComponentProps;
    enablePagination?: boolean;
    showExpandableSearch?: boolean;
    showMultiSort?: boolean;
    showRichFilter?: boolean;
    quickFilters?: QFContainer[];
    setGlobalFilter: (filterValue: ITableFilter) => void;
    columns: Column<D>[];
    setSortBy: (a: any[]) => void;
    showRuleDropdown?: boolean;
    selectViewOption?: string;
    setSelectViewOption?: Function;
}

const TableFunctions = <D extends object>({
    paginationProps,
    enablePagination,
    showExpandableSearch,
    showMultiSort,
    showRichFilter,
    setGlobalFilter,
    setSortBy,
    columns,
    state,
    quickFilters,
    showRuleDropdown,
    selectViewOption,
    setSelectViewOption
}: Props<D>) => {
    const { globalFilter } = state as { globalFilter: ITableFilter };
    const stateQuickFilters = globalFilter?.quickFilters ?? [];

    const updateGlobalFilter = (filterKey: string, newFilterVal: any) => {
        setGlobalFilter({
            ...globalFilter,
            [filterKey]: newFilterVal
        });
        paginationProps.gotoPage(0);
    };

    return (
        <>
            <div className={styles.tableFunctionsRow}>
                <div className={styles.tableButtons}>
                    {quickFilters &&
                        quickFilters.length > 0 &&
                        quickFilters.map(({ id, data }) => (
                            <Table.QuickFilters
                                dataTestId={id}
                                key={id}
                                onlyOneCanBeSelected={false}
                                availableQuickFilters={data}
                                selectedQuickFilters={stateQuickFilters?.find((v) => v.id === id)?.data ?? []}
                                onChange={(key, val) => {
                                    const newQf = [...stateQuickFilters];
                                    const foundIndex = _.findIndex(newQf, ['id', id]);
                                    if (foundIndex === -1) {
                                        newQf.push({ id, data: val });
                                    } else {
                                        newQf[foundIndex] = { id, data: val };
                                    }
                                    setGlobalFilter({
                                        ...globalFilter,
                                        [key]: newQf
                                    });
                                    paginationProps.gotoPage(0);
                                }}
                            />
                        ))}
                    {showRichFilter && (
                        <Table.RichFilter
                            isActive={Boolean(state.globalFilter?.richFilters?.length)}
                            columns={columns as Required<Pick<Column, 'Header' | 'dataType' | 'id'>>[]}
                            initialFilters={state.globalFilter?.richFilters}
                            updateGlobalFilter={updateGlobalFilter}
                            allowPan
                        />
                    )}
                    {showMultiSort && (
                        <Table.SortModal
                            isActive={Boolean(state.sortBy?.length)}
                            options={columns
                                .filter((aCol) => !aCol.disableSortBy)
                                .map((o) => ({
                                    id: String(o.id),
                                    selectChild: o.title ?? o.Header
                                }))}
                            tableTitle='Sort Table'
                            onCloseText='Cancel'
                            onActionText='Sort Table'
                            addSortColumnText='Add another column sort'
                            initialSortColumns={state.sortBy}
                            onSort={(aVal) => {
                                setSortBy(aVal);
                            }}
                        />
                    )}

                    <div className={styles.searchContainer}>
                        {showExpandableSearch && (
                            <Table.ExpandableSearch
                                initialValue={state.globalFilter?.searchBarValue}
                                id='expandableSearch'
                                onCollapse={() => {
                                    setGlobalFilter({
                                        ...state.globalFilter,
                                        searchBarValue: ''
                                    });
                                }}
                                onSearch={(searchBarValue: string) => {
                                    setGlobalFilter({
                                        ...state.globalFilter,
                                        searchBarValue
                                    });
                                    paginationProps.gotoPage(0);
                                }}
                                allowPan
                            />
                        )}
                    </div>
                </div>
                <div>{enablePagination && <Pagination {...paginationProps} />}</div>
                {showRuleDropdown && selectViewOption && setSelectViewOption && (
                    <TableDropDown selectViewOption={selectViewOption} setSelectViewOption={setSelectViewOption} />
                )}
            </div>

            {showRichFilter && state.globalFilter?.richFilters && state.globalFilter.richFilters.length > 0 && (
                <div className={styles.filterBadesRow}>
                    <Table.FilterBadges
                        currentFilters={state.globalFilter?.richFilters}
                        dateFormatFn={DateService.getDate}
                        numberFormatFn={NumberService.getLocaleNumber}
                        updateGlobalFilter={updateGlobalFilter}
                    />
                </div>
            )}
        </>
    );
};

export default TableFunctions;
